import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "../../components/ui/Controls";
import { AlertBanner, EmptyState, ErrorState, Skeleton } from "../../components/ui/Feedback";
import { buildReviewsApi } from "../../lib/api";
import { BuildPlanReview } from "./BuildPlanReview";
import { JobProgress } from "./JobProgress";
import { ReleaseHistory } from "./ReleaseHistory";
import { SourceSetup } from "./SourceSetup";
import { OperationsCockpit } from "../operations/OperationsCockpit";

export interface ProjectWorkspaceProps {
  readonly projectId: string;
}

export function ProjectWorkspace({ projectId }: ProjectWorkspaceProps) {
  const queryClient = useQueryClient();
  const [activeJobId, setActiveJobId] = useState<string>();
  const reviews = useQuery({
    queryKey: ["build-reviews", projectId],
    queryFn: ({ signal }) => buildReviewsApi.list(projectId, signal),
    retry: false,
  });

  const refreshReviews = () => {
    void queryClient.invalidateQueries({ queryKey: ["build-reviews", projectId] });
  };

  return (
    <div className="stack project-workspace">
      <AlertBanner heading="Yayın güvenlik zinciri" tone="info">
        <p>
          Kaynak önce karantinaya alınır, zararlı yazılım taramasından geçer ve değişmez kimlikle
          kaydedilir. Build planı açıkça onaylanmadan kod çalıştırılmaz; sağlık kontrolü geçmeden
          etkin release değiştirilmez.
        </p>
      </AlertBanner>

      <SourceSetup onUploadAccepted={setActiveJobId} projectId={projectId} />

      {activeJobId === undefined ? null : (
        <section aria-labelledby="accepted-job-heading" className="stack">
          <div className="split-row">
            <div>
              <p className="eyebrow">Canlı işlem</p>
              <h2 id="accepted-job-heading">Kaynak kabulü ve build ilerlemesi</h2>
            </div>
            <Button
              onClick={() => {
                setActiveJobId(undefined);
              }}
              variant="quiet"
            >
              Görünümü kapat
            </Button>
          </div>
          <JobProgress jobId={activeJobId} />
        </section>
      )}

      <section aria-labelledby="build-review-list-heading" className="stack">
        <div className="split-row">
          <div>
            <p className="eyebrow">İnsan onayı</p>
            <h2 id="build-review-list-heading">Build planları</h2>
            <p className="muted">
              Framework, sabit komutlar, çıktı biçimi, limitler ve ağ politikası sunucunun kapalı
              seçeneklerinden biri olarak gösterilir.
            </p>
          </div>
          <Button onClick={refreshReviews} variant="secondary">
            Yenile
          </Button>
        </div>
        {reviews.isLoading ? <Skeleton label="Build planları yükleniyor" /> : null}
        {reviews.isError ? <ErrorState onRetry={refreshReviews} /> : null}
        {reviews.data?.items.length === 0 ? (
          <EmptyState
            body="Temiz bir Git snapshot'ı veya kaynak ZIP'i kabul edildiğinde algılanan plan burada görünür."
            heading="Onay bekleyen build planı yok"
          />
        ) : null}
        {reviews.data?.items.map((review) => (
          <BuildPlanReview
            key={review.id}
            onApproved={(approved) => {
              refreshReviews();
              void queryClient.invalidateQueries({ queryKey: ["releases", projectId] });
              if (approved.status === "approved") setActiveJobId(undefined);
            }}
            onRefresh={refreshReviews}
            review={review}
          />
        ))}
      </section>

      <ReleaseHistory projectId={projectId} />
      <OperationsCockpit projectId={projectId} />
    </div>
  );
}
